import { Stack, Text, VStack } from '@chakra-ui/react';
import { endOfMonth } from 'date-fns/endOfMonth';
import { endOfYear } from 'date-fns/endOfYear';
import { getMonth } from 'date-fns/getMonth';
import { getYear } from 'date-fns/getYear';
import { startOfYear } from 'date-fns/startOfYear';
import { useEffect, useState } from 'react';
import { MdOutlineFileDownload } from 'react-icons/md';
import {
  useDownloadRevenueJournalV2,
  useDownloadRevenueScheduleV2,
} from '../../api/revenueService';
import {
  MBox,
  MButton,
  MFormField,
  MPageContainer,
  MPageHeader,
  MRadio,
  MRadioGroup,
} from '../../components/Monetize';
import { DatePicker } from '../../components/Monetize/DatePicker/DatePicker';
import { useDocumentHead } from '../../services/documentHead';
import { logger } from '../../services/logger';
import { downloadBlobAsFile } from '../../utils/download';
import { AccountingPeriodLockHeader } from './AccountingPeriodLockHeader';

type ViewMode = 'month' | 'year' | 'all';

export const RevRecPageV2 = () => {
  const { setDocTitle } = useDocumentHead();
  useEffect(() => {
    setDocTitle('Revenue Recognition');
  }, [setDocTitle]);

  const [viewMode, setViewMode] = useState<ViewMode>('month');
  const [selectedDate, setSelectedDate] = useState('');

  const handleViewModeChange = (value: ViewMode) => {
    setViewMode(value);
    setSelectedDate('');
  };

  const getApiPayload = () => {
    const year = getYear(selectedDate);
    const month = getMonth(selectedDate) + 1;

    switch (viewMode) {
      case 'month':
        return {
          type: 'BY_MONTH' as const,
          year,
          month,
        };
      case 'year':
        return {
          type: 'BY_YEAR' as const,
          year,
        };
      case 'all':
        return {
          type: 'ALL_TIME' as const,
        };
      default:
        return {
          type: 'BY_MONTH' as const,
          year,
          month,
        };
    }
  };

  const { isPending: isDownloadingSchedule, mutateAsync: getRevenueSchedule } =
    useDownloadRevenueScheduleV2({ meta: { showErrorToast: true } });

  const { isPending: isDownloadingJournal, mutateAsync: getRevenueJournal } =
    useDownloadRevenueJournalV2({ meta: { showErrorToast: true } });

  const handleDownloadSchedule = async () => {
    try {
      const payload = getApiPayload();
      const result = await getRevenueSchedule(payload);

      downloadBlobAsFile(result.data, result.filename, 'text/csv');
    } catch (error) {
      logger.error('Error downloading revenue schedule:', error);
    }
  };

  const handleDownloadJournal = async () => {
    try {
      const payload = getApiPayload();
      const result = await getRevenueJournal(payload);
      downloadBlobAsFile(result.data, result.filename, 'text/csv');
    } catch (error) {
      logger.error('Error downloading revenue journal:', error);
    }
  };

  const renderPeriodSelector = () => {
    const currentDate = new Date();
    const currentYear = getYear(currentDate);

    switch (viewMode) {
      case 'month':
        return (
          <MFormField label="Month">
            <DatePicker
              mode="MONTH"
              value={selectedDate}
              onChange={(dateString) => {
                if (dateString) {
                  setSelectedDate(dateString);
                }
              }}
              placeholder="Select month"
              maxDate={endOfMonth(currentDate)}
              useCustomFormatting
            />
          </MFormField>
        );
      case 'year':
        return (
          <MFormField label="Year">
            <DatePicker
              mode="YEAR"
              value={selectedDate}
              onChange={(dateString) => {
                if (dateString) {
                  setSelectedDate(dateString);
                }
              }}
              placeholder="Select year"
              minDate={startOfYear(new Date(currentYear, 0, 1))}
              maxDate={endOfYear(new Date(currentYear, 11, 31))}
              useCustomFormatting
            />
          </MFormField>
        );
      case 'all':
        return (
          <Text fontWeight="bold" fontSize="sm" color="tPurple.base">
            All Time
          </Text>
        );
      default:
        return null;
    }
  };
  return (
    <MPageContainer enableAccountSearch maxWidth="100%" data-testid="revenue">
      <MPageHeader title="Revenue Recognition">
        <AccountingPeriodLockHeader />
      </MPageHeader>
      <MBox w="100%" h="100%" pt={6}>
        <MBox mb={6}>
          <MRadioGroup
            onChange={(value) => handleViewModeChange(value as ViewMode)}
            value={viewMode}
            colorPalette="purple"
          >
            <Stack direction="column" gap={2}>
              <MRadio value="month">By month</MRadio>
              <MRadio value="year">By year</MRadio>
              <MRadio value="all">All time</MRadio>
            </Stack>
          </MRadioGroup>
        </MBox>

        <MBox mb={6} width="20%">
          {renderPeriodSelector()}
        </MBox>

        <VStack gap={2} alignItems="flex-start">
          <MButton
            variant="primary"
            onClick={handleDownloadSchedule}
            leftIcon={<MdOutlineFileDownload />}
            minWidth={250}
            isLoading={isDownloadingSchedule}
            isDisabled={
              (!selectedDate && viewMode !== 'all') ||
              isDownloadingSchedule ||
              isDownloadingJournal
            }
          >
            Download Revenue Schedule
          </MButton>
          <MButton
            variant="primary"
            onClick={handleDownloadJournal}
            leftIcon={<MdOutlineFileDownload />}
            minWidth={250}
            isLoading={isDownloadingJournal}
            isDisabled={
              (!selectedDate && viewMode !== 'all') ||
              isDownloadingSchedule ||
              isDownloadingJournal
            }
          >
            Download Revenue Journal
          </MButton>
        </VStack>
      </MBox>
    </MPageContainer>
  );
};
